import { parseApiError, type Lead, type ParsedApiError } from '../../services/api.ts';

export interface CrmSaleFailure {
  title: string;
  description?: string;
  message: string;
  guidance: string;
  canCorrectPhone: boolean;
}

const correctionMessages: Record<string, string> = {
  invalid_phone: 'Informe um telefone válido com DDD.',
  whatsapp_number_not_found: 'Este número não foi encontrado no WhatsApp. Confira o DDD e o número.',
  whatsapp_validation_unavailable: 'Não foi possível verificar o WhatsApp agora. Aguarde alguns instantes e tente novamente.',
  whatsapp_identity_ambiguous: 'Foram encontrados destinos diferentes para este número. Confira o telefone antes de continuar.',
  whatsapp_identity_conflict: 'Este telefone já pertence a outro cadastro da clínica. Confira o número. Nenhum dado foi alterado.',
  phone_correction_expected_phone_required: 'Atualize os dados do cadastro antes de corrigir o telefone.',
  phone_correction_stale: 'O telefone foi alterado por outra pessoa. Recarregue o cadastro antes de continuar.',
  phone_correction_contact_missing: 'O vínculo deste cadastro precisa ser conferido pelo suporte antes da correção. Nenhum dado foi alterado.',
  phone_correction_identity_conflict: 'Os vínculos deste cadastro precisam ser conferidos pelo suporte. Nenhum dado foi alterado.',
  phone_correction_delivery_in_progress: 'Há um envio em andamento para este contato. Aguarde a conclusão e tente novamente.',
  phone_correction_capture_in_progress: 'Há uma mensagem em processamento para este contato. Aguarde a conclusão e tente novamente.',
  phone_correction_capture_unavailable: 'Não foi possível confirmar se há mensagens em processamento. Aguarde e tente novamente.',
  phone_correction_busy: 'Este cadastro está sendo atualizado ou possui um envio em andamento. Aguarde e tente novamente.',
  contact_phone_change_in_progress: 'Este contato está em atendimento. Aguarde a conclusão para corrigir o telefone.',
  phone_history_conflict: 'O histórico deste cadastro precisa ser conferido pelo suporte antes da correção. Nenhum histórico será combinado.',
  phone_history_schema_unavailable: 'A correção de telefone está temporariamente indisponível. Tente novamente mais tarde.',
  instagram_phone_attribute_unavailable: 'Este contato é do Instagram. A alteração do telefone deste cadastro ainda não está disponível.',
  lead_update_failed: 'Não foi possível atualizar o telefone. Nenhuma alteração foi salva. Tente novamente mais tarde.',
  lead_update_confirmation_unavailable: 'A atualização foi salva, mas não foi possível carregar o cadastro atualizado. Recarregue o cadastro antes de continuar.',
};

function allowedMessage(messages: Record<string, string>, key?: string): string | undefined {
  return key && Object.prototype.hasOwnProperty.call(messages, key) ? messages[key] : undefined;
}

export function getPhoneCorrectionError(error: unknown): ParsedApiError {
  const parsed = parseApiError(error);
  return {
    code: parsed.code,
    status: parsed.status,
    message: allowedMessage(correctionMessages, parsed.code) ||
      'Não foi possível confirmar a atualização. Confira o cadastro antes de tentar novamente.',
  };
}

export function getCrmSaleFailure(error: unknown): CrmSaleFailure {
  const parsed = parseApiError(error);
  if (parsed.message === 'O lead vinculado não possui um telefone válido.' || parsed.code === 'invalid_phone') {
    return {
      title: 'Corrija o telefone para registrar a venda',
      description: 'O telefone do lead vinculado à venda está inválido.',
      message: 'O telefone deste cadastro está inválido.',
      guidance: 'Corrija o telefone para continuar. Os dados preenchidos na venda serão mantidos.',
      canCorrectPhone: true,
    };
  }
  const knownReasons: Record<string, string> = {
    'O telefone informado é inválido.': 'O telefone do comparecimento está inválido. Confira o cadastro antes de continuar.',
    'O telefone informado não pertence ao lead vinculado.': 'O telefone do comparecimento está diferente do cadastro. Atualize os dados e confira o vínculo.',
    'Lead ou comparecimento não encontrado para este cliente/clínica.': 'O cadastro ou comparecimento não foi encontrado. Confira os dados e seu acesso ao registro.',
    'O comparecimento informado não pertence ao lead informado.': 'Este comparecimento não pertence ao cadastro selecionado. Peça ao suporte para conferir o vínculo.',
    'O comparecimento informado não possui um agendamento válido para o lead.': 'Este comparecimento não possui um agendamento válido. Peça ao suporte para conferir o vínculo.',
  };
  const knownMessage = allowedMessage(knownReasons, parsed.message);
  if (knownMessage) {
    return { title: 'Não foi possível registrar a venda', message: knownMessage,
      guidance: 'Os dados preenchidos foram mantidos. Volte ao formulário para conferir o registro.', canCorrectPhone: false };
  }
  if (parsed.status === 422) {
    return { title: 'Confira os dados da venda', message: 'Há um campo da venda inválido.',
      guidance: 'Revise a data, a hora e os valores antes de continuar. Os dados preenchidos foram mantidos.', canCorrectPhone: false };
  }
  return {
    title: 'Não foi possível confirmar o registro',
    message: 'Não foi possível confirmar se a venda foi registrada.',
    guidance: 'Confira a aba Vendas antes de tentar novamente. Os dados preenchidos foram mantidos.',
    canCorrectPhone: false,
  };
}

export function getPhoneCorrectionFeedback(lead: Lead): string {
  if (lead.phone_verification?.status === 'verified') return 'Telefone atualizado e verificado no WhatsApp da clínica.';
  if (lead.phone_verification?.status === 'not_connected') {
    return 'Telefone atualizado. O WhatsApp da clínica não está conectado; o número não foi verificado.';
  }
  return 'Telefone atualizado. Não foi possível confirmar a verificação do WhatsApp.';
}

export function getCrmPhoneLeadId(contact: { id?: number; lead_id?: number }, stage: string): number | null {
  const id = stage === 'leads' ? contact.id : contact.lead_id;
  return Number.isInteger(id) && Number(id) > 0 ? Number(id) : null;
}

export function reconcileCrmPhone<T extends { id?: number; lead_id?: number; phone?: string | null }>(record: T | null, lead: Lead, isLead = false): T | null {
  if (!record || (isLead ? record.id : record.lead_id) !== lead.id) return record;
  return { ...record, phone: lead.phone };
}
